import { useRef, useState, useCallback } from 'react';
import { FileText } from 'lucide-react';
import {
  AI_ACCEPTED_EXTENSIONS,
  AI_LIBRARY_FILE_CAP,
  AI_MAX_FILE_SIZE_MB
} from './constants';

const acceptAttribute = AI_ACCEPTED_EXTENSIONS.map((ext) => `.${ext}`).join(',');

/**
 * Drag-and-drop / click upload for process AI documents (PDF, DOCX).
 * Used in AI-assisted create and process edit flowchart panels.
 */
export function ProcessDocumentUploadZone({
  onFilesSelected,
  disabled = false,
  /** Max files allowed in the library (footnote + parent cap). */
  maxFiles = AI_LIBRARY_FILE_CAP,
  /** `compact` — inline row under document library; `expanded` — standalone create flow. */
  variant = 'compact',
  /** When true, render nothing if disabled (e.g. library at file cap). */
  hiddenWhenDisabled = false,
  className = ''
}) {
  const limitsFootnote = `Up to ${maxFiles} files · ${AI_MAX_FILE_SIZE_MB} MB max`;
  const fileInputRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);

  const handleFiles = useCallback(
    (fileList) => {
      if (disabled || !fileList?.length) return;
      onFilesSelected(fileList);
    },
    [disabled, onFilesSelected]
  );

  const openFilePicker = useCallback(() => {
    if (!disabled) fileInputRef.current?.click();
  }, [disabled]);

  const handleDrop = useCallback(
    (event) => {
      event.preventDefault();
      setIsDragging(false);
      handleFiles(event.dataTransfer.files);
    },
    [handleFiles]
  );

  if (hiddenWhenDisabled && disabled) {
    return null;
  }

  const isExpanded = variant === 'expanded';
  const canInteract = !disabled;

  const zoneClassName = [
    'border-2 border-dashed text-center transition-colors',
    isExpanded
      ? 'mb-3 cursor-pointer rounded-xl p-8'
      : 'rounded-lg px-4 py-6',
    !canInteract
      ? 'cursor-not-allowed border-gray-200 bg-gray-50 text-gray-400'
      : isDragging
        ? 'cursor-pointer border-blue-400 bg-blue-50'
        : isExpanded
          ? 'cursor-pointer border-gray-300 bg-gray-50 hover:border-blue-300 hover:bg-blue-50/50'
          : 'cursor-pointer border-gray-300 bg-gray-50/80 text-gray-500 hover:border-blue-300 hover:bg-blue-50/50',
    className
  ]
    .filter(Boolean)
    .join(' ');

  const title = isExpanded ? 'Add to cycle library' : '+ Upload document';

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        accept={acceptAttribute}
        multiple
        className="hidden"
        disabled={disabled}
        onChange={(event) => {
          handleFiles(event.target.files);
          event.target.value = '';
        }}
      />
      <div
        role="button"
        tabIndex={canInteract ? 0 : -1}
        aria-disabled={disabled}
        onClick={openFilePicker}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            openFilePicker();
          }
        }}
        onDragEnter={(event) => {
          event.preventDefault();
          if (canInteract) setIsDragging(true);
        }}
        onDragOver={(event) => event.preventDefault()}
        onDragLeave={() => setIsDragging(false)}
        onDrop={canInteract ? handleDrop : undefined}
        className={zoneClassName}
      >
        {isExpanded ? <FileText className="mx-auto mb-3 h-8 w-8 text-gray-400" /> : null}
        <p className={`${isExpanded ? 'text-sm font-semibold text-gray-900' : 'text-sm font-medium text-gray-600'}`}>
          {title}
        </p>
        {isExpanded ? (
          <p className="mt-1 text-sm text-gray-500">PDF or DOCX · click or drag here</p>
        ) : null}
        <p className={`${isExpanded ? 'mt-2' : 'mt-1'} text-xs text-gray-400`}>{limitsFootnote}</p>
      </div>
    </>
  );
}

export default ProcessDocumentUploadZone;
